import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { useState } from "react";
import { GD } from "src/GD";
import { clearAuthStorage } from "src/utils/authStorage";
import { FormParser } from "src/view/FormParser";
import { StartScreenView } from "../StartScreenView";
import {
	AUTH_FORM_ACTIONS_SX,
	AUTH_PRIMARY_BUTTON_SX,
	AUTH_SECONDARY_BUTTON_SX,
	AUTH_TWO_COLUMN_SX,
	AuthCard,
	AuthDivider,
	AuthStatus,
} from "./AuthLayout";
import { S_REGISTRATION_COMPLETE } from "./AuthView";

const formShape = {
	password: "",
	confirm_password: "",
	$__password_type: "password",
	$__password_autoComplete: "new-password",
	$__password_label: "Create password",
	$__confirm_password_type: "password",
	$__confirm_password_autoComplete: "new-password",
	$__confirm_password_label: "Confirm password",
	$__confirm_password_details: "Repeat password",
	$__decorator: {
		blocks: [{ order: ["password", "confirm_password"], blockless: true, sx: AUTH_TWO_COLUMN_SX }],
	},
};

const isActiveAccountConflict = (error: IError): boolean => {
	const text = `${error.message ?? ""} ${String(error.details ?? "")}`;
	return error.code === 403 && /not permitted for a private (?:account|company)|private (?:account|company)/i.test(text);
};

export const InviteSignupPage = ({ token }: { token: string }) => {
	const [busy, setBusy] = useState(false);
	const [res] = GD.REQ_INVITE_GET.useRequest(undefined, { code: token });
	const invite = res?.data;
	const error = res?.error;

	const onSubmit = async (data: typeof formShape) => {
		if (data.password !== data.confirm_password) {
			return { data, error: { code: 0, message: "Passwords do not match" } };
		}

		setBusy(true);
		const result = await GD.REQ_INVITE_SIGNUP.request({ token, password: data.password });
		if (result.error) {
			setBusy(false);
			return { data, error: result.error };
		}

		const loginResult = await GD.REQ_AUTH_LOGIN.request({
			login: invite!.email,
			password: data.password,
			controls: { remember: false },
		});
		setBusy(false);
		window.history.replaceState({}, "", "/");

		if (loginResult) {
			S_REGISTRATION_COMPLETE.invoke({ login: invite!.email, pass: data.password });
			return { data };
		}

		window.dispatchEvent(new PopStateEvent("popstate"));
		return { data };
	};

	if (error) {
		const activeAccountConflict = isActiveAccountConflict(error);
		const signOutAndRetry = () => {
			// Public invitation routes can be opened while another account is stored in
			// this browser. Clear only Cardpartner auth state, then retry the same URL.
			clearAuthStorage();
			window.location.reload();
		};
		return (
			<StartScreenView>
				<AuthCard
					icon={activeAccountConflict ? "warning" : "error"}
					eyebrow="Invitation registration"
					title={activeAccountConflict ? "Sign out to use this invitation" : "Invitation unavailable"}
					subtitle={activeAccountConflict
						? "Another Cardpartner account is active in this browser."
						: "We could not open this registration request."}>
					<AuthStatus tone="warning" title={activeAccountConflict ? "Another account is signed in" : "This link cannot be used"}>
						{activeAccountConflict
							? "Sign out of the current account, then continue with this invitation. The invitation link will stay open."
							: "The invitation may have expired, already been used, or been replaced. Ask the workspace administrator for a new invitation."}
					</AuthStatus>
					<AuthDivider />
					<Button
						title={activeAccountConflict ? "Sign out and continue" : "Back to sign in"}
						variant={activeAccountConflict ? "primary" : "secondary"}
						onClick={activeAccountConflict ? signOutAndRetry : () => (window.location.href = "/")}
						sx={activeAccountConflict ? AUTH_PRIMARY_BUTTON_SX : AUTH_SECONDARY_BUTTON_SX}
					/>
				</AuthCard>
			</StartScreenView>
		);
	}

	if (!invite) {
		return (
			<StartScreenView busy>
				<AuthCard icon="loading" size="wide" eyebrow="Invitation registration" title="Loading your invitation" subtitle="We are checking the account details attached to this link.">
					<AuthStatus title="Checking invitation">Please wait a moment.</AuthStatus>
				</AuthCard>
			</StartScreenView>
		);
	}

	return (
		<StartScreenView busy={busy}>
			<AuthCard
				icon="mail"
				size="wide"
				eyebrow="Invitation registration"
				title="Complete your account"
				subtitle="Create a password to activate the invited workspace account.">
				<>
						<AuthStatus title="Invited account">
							You have been invited as <b>{invite.name}</b> ({invite.email}).
						</AuthStatus>
						<Spacer size="22px" />
						<FormParser
							key="inviteSignupForm"
							ignoreObserver={true}
							fullSizeButtons={true}
							secondaryButton={null}
							actionsSx={AUTH_FORM_ACTIONS_SX}
							primaryButton={{ title: "Set password", variant: "primary", sx: { ...AUTH_PRIMARY_BUTTON_SX, flexGrow: 1 } }}
							onSubmit={onSubmit}
							onRequest={async () => ({ ...formShape })}
						/>
						<AuthDivider label="Already have access?" />
						<Button
							title="Back to sign in"
							variant="secondary"
							onClick={() => (window.location.href = "/")}
							sx={AUTH_SECONDARY_BUTTON_SX}
						/>
				</>
			</AuthCard>
		</StartScreenView>
	);
};
